import {
  ComponentProps,
  ElementType,
  Fragment,
  ReactElement,
  cloneElement,
  createElement,
  isValidElement,
} from 'react';

import { omit } from 'underscore';

type PropsOf<TTag = any> = TTag extends ElementType
  ? ComponentProps<TTag>
  : never;

export function render<TTag extends ElementType, TSlot>(
  props: PropsOf<TTag> & { ref?: unknown },
  slot: TSlot = {} as TSlot,
  tag: ElementType,
  name: string
) {
  const { as: Component = tag, children, ...passthroughProps } = props;
  const resolvedChildren = (
    typeof children === 'function' ? children(slot) : children
  ) as ReactElement | ReactElement[];

  if (Component === Fragment) {
    if (Object.keys(passthroughProps).length > 0) {
      if (
        !isValidElement(resolvedChildren) ||
        (Array.isArray(resolvedChildren) && resolvedChildren.length > 1)
      ) {
        throw new Error(
          [
            'Passing props on "Fragment"!',
            '',
            `The current component <${name} /> is rendering a "Fragment".`,
            `However we need to passthrough the following props:`,
            Object.keys(passthroughProps)
              .map((line) => `  - ${line}`)
              .join('\n'),
            '',
            'You can apply a few solutions:',
            [
              'Add an `as="..."` prop, to ensure that we render an actual element instead of a "Fragment".',
              'Render a single element as the child so that we can forward the props onto that element.',
            ]
              .map((line) => `  - ${line}`)
              .join('\n'),
          ].join('\n')
        );
      }

      return cloneElement(
        resolvedChildren,
        Object.assign({}, omit(passthroughProps, ['ref']))
      );
    }
  }

  return createElement(
    Component,
    Object.assign({}, omit(passthroughProps, ['ref'])),
    resolvedChildren
  );
}
